import { useCallback, useMemo, useState } from 'react';
import type {
    DealerPeriodStatement,
    FundAccountSelection,
    PairPeriodStatement,
    StatementPeriod,
} from '../types/dealerFundStatementPeriod.types';
import { fundAccountKey, type FundAccountRow } from '../utils/fundAccountList';
import { cardState, type FundAccountCardState } from '../utils/fundAccountCardState';
import { fundAccountStatementPath } from '../utils/fundAccountStatementRoute';
import { describePeriod, fundAccountCardTitle, formatStatementDates } from '../utils/fundStatementLabels';
import { groupStatementBlocks, type StatementLayout } from '../utils/statementBlocks';
import { useFundAccountList, type FundAccountListView } from './useFundAccountList';
import { usePairPeriodStatementQuery } from './usePairPeriodStatementQuery';
import { useProductNames } from './useProductNames';

export type { FundAccountCardState };

export interface FundAccountsModel {
    list: FundAccountListView;
    currency: string;
    selectedKey: string | null;
    onSelect: (row: FundAccountRow) => void;
}

export interface FundAccountCardModel {
    state: FundAccountCardState;
    title: string | null;
    periodLabel: string;
    datesLine: string | null;
    statement: PairPeriodStatement | null;
    layout: StatementLayout | null;
    detailsHref: string | null;
    onRetry: () => void;
}

const NO_PAIRS: DealerPeriodStatement['pairs'] = [];

export function useFundAccountsSection(
    dealerId: string,
    statement: DealerPeriodStatement | null,
    period: StatementPeriod,
    todayIso: string,
) {
    const [selection, setSelection] = useState<FundAccountSelection | null>(null);
    const pairs = statement?.pairs ?? NO_PAIRS;
    const productNames = useProductNames(pairs.length > 0);
    const list = useFundAccountList(pairs, productNames);
    const query = usePairPeriodStatementQuery(selection, period, todayIso);
    const periodLabel = describePeriod(period, todayIso);

    const selectedRow = useMemo(
        () => (selection ? list.allRows.find((row) => row.key === fundAccountKey(selection)) ?? null : null),
        [selection, list.allRows],
    );
    const result = query.data;
    const pairStatement = result?.status === 'ok' ? result.statement : null;
    const layout = useMemo(
        () => (pairStatement ? groupStatementBlocks(pairStatement.rows) : null),
        [pairStatement],
    );
    const { refetch } = query;
    const onRetry = useCallback(() => { void refetch({ cancelRefetch: false }); }, [refetch]);
    const onSelect = useCallback((row: FundAccountRow) => {
        if (row.selectable) setSelection({ sellingDealerId: row.sellingDealerId, productId: row.productId });
    }, []);
    const clearSelection = useCallback(() => setSelection(null), []);

    const accounts: FundAccountsModel = {
        list,
        currency: statement?.currency ?? 'GBP',
        selectedKey: selection ? fundAccountKey(selection) : null,
        onSelect,
    };
    const state = cardState(selectedRow !== null, query.isLoading, query.isError, result?.status);
    const card: FundAccountCardModel = {
        state,
        title: selectedRow ? fundAccountCardTitle(selectedRow) : null,
        periodLabel,
        datesLine: pairStatement ? formatStatementDates(pairStatement.from, pairStatement.to) : null,
        statement: pairStatement,
        layout,
        detailsHref: state === 'ok' && selection ? fundAccountStatementPath(dealerId, selection, period) : null,
        onRetry,
    };
    return { accounts, card, clearSelection };
}
